import React, { Fragment } from 'react';

import { FormControl, FormHelperText } from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import clsx from 'clsx';

import { getDate } from '@homesusa/core';
import { FormMode } from '../../enums';
import { DatePickerProps } from '../../interfaces';
import { isValidDate, checkMaxDate, checkMinDate } from '../../validations';
import { referencedDate } from '../../utils';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { FormContext, FormRefsContext } from '../../contexts';
import { TooltipContainer } from '../tooltip-container';

export function DatePicker({ name, ...props }: DatePickerProps): JSX.Element {
  const contextRules = useGetFieldRules<DatePickerProps>(name);
  return <DatePickerComponent {...{ ...contextRules, ...props }} name={name} />;
}

const calendarIconWidth = 56;

function DatePickerComponent(props: DatePickerProps): JSX.Element {
  const [selected, setSelected] = React.useState<Date | null>();
  const { formMode } = React.useContext(FormContext);
  const { fieldsRefs } = React.useContext(FormRefsContext);
  const { setValueByName } = useFormContext();
  const [open, setOpen] = React.useState(false);

  const setDate = (value: Date | null): void => {
    setSelected(value);
    setValueByName(props.name, value);
    validateInput(value);
    if (props.onChange) {
      props.onChange(value ?? undefined);
    }
  };

  React.useEffect(() => {
    const date = getDate(props.defaultValue);
    if (date) {
      setDate(date);
    }
  }, []);

  const getError = (value: unknown): string[] | null => {
    const errorMessages = props.customFieldProps?.errorMessages;
    if (value && !isValidDate(value)) {
      return [errorMessages?.validDate ?? 'Invalid Date'];
    }

    const valueDate = new Date((value as Date).setHours(0, 0, 0, 0));

    if (props.minDate && checkMinDate(valueDate, props.minDate, false)) {
      return [
        props.customFieldProps?.errorMessages?.minDate ??
          `Must be on or after ${referencedDate(props.minDate)}`
      ];
    }

    if (props.maxDate && checkMaxDate(valueDate, props.maxDate, false)) {
      setDate(null);
      return null;
    }

    return null;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (props.disableKeyboard) {
      event.preventDefault();
      setOpen(true);
    }
  };

  const { error, validateInput } = useValidateInput(props, getError);

  return (
    <TooltipContainer tooltip={props.tooltip} sx={props.sx}>
      <FormControl
        className={clsx(
          {
            'form-field': true,
            'required-submit': props.requiredToSubmit
          },
          props.className
        )}
        id={props.id}
        sx={{ '& label': { maxWidth: `calc(100% - ${calendarIconWidth}px)` } }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <MuiDatePicker
            format='MM/dd/yyyy'
            label={props.label}
            open={open}
            onOpen={(): void => setOpen(true)}
            onClose={(): void => setOpen(false)}
            value={selected ?? null}
            onChange={(date: Date | null): void => setDate(date)}
            disabled={props.disabled || formMode === FormMode.ReadOnly}
            slotProps={{
              textField: {
                size: props.size ?? 'medium',
                variant: 'outlined',
                error: error != null,
                helperText: error ? error : undefined,
                onKeyDown: onKeyDown,
                required: props.required,
                name: props.name
              }
            }}
            minDate={props.minDate}
            maxDate={props.maxDate}
            shouldDisableDate={props.shouldDisableDate}
            ref={(el: HTMLInputElement) => {
              if (fieldsRefs && props?.name) {
                fieldsRefs.current[props.name] = el;
              }
            }}
          />
          {props.helperText && <FormHelperText>{props.helperText}</FormHelperText>}
          {error == null && selected == null && (
            <Fragment>
              {props.maxDate && (
                <FormHelperText>
                  {props.customFieldProps?.errorMessages?.maxDate ??
                    `Must be on or before ${referencedDate(props.maxDate)}`}
                </FormHelperText>
              )}
            </Fragment>
          )}
        </LocalizationProvider>
      </FormControl>
    </TooltipContainer>
  );
}
